import { partnerVariantShortLabel } from "@/utils/partnerVariant";
import type { PartnerVariant } from "@/types/LoginInfo";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
// 3rd parties
import { Button, ButtonGroup, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useWindowSize } from "react-use";
import styled from "styled-components";
import { SntSelectWidget, type SntSelectLabelContext, type SntSelectOption } from "@cleanup/widgets/selectors/SntSelect";
// owner
import organizationClient from "@apis/organizationClient";
import globalSearchClient from "@apis/globalSearchClient";
import useUser from "@hooks/useUser";
import useErrorDialog from "@/cleanup/widgets/dialog/useErrorDialog";

import { PrimaryTag, GreyTag, DangerTag } from "@cleanup/components/TagMore";
import OrganizationType from "@/cleanup/utils/OrganizationType";
import SntOrgBrandIcon from "@widgets/images/SntOrgBrandIcon";
import SntOrgIcon from "@icons/SntOrgIcon";
import SntPartnersIcon from "@icons/SntPartnersIcon";
import SntGlobeIcon from "@icons/SntGlobeIcon";
import SntStockOrgIcon from "@icons/SntStockOrgIcon";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import SntSystemDevIcon from "@cleanup/components/Icons/SntSystemDevIcon";
import HeaderIconButton from "@/containers/AdminLayout/Header/HeaderIconButton";
import { userSelector } from "@redux/selectors";
import { useCustomBranding } from "@/pages/CustomBranding/useCustomBranding";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import SntButtonGroup from "@/cleanup/widgets/forms/SntButtonGroup";
import SntOnOffSwitch from "@/cleanup/wrappers/Switch/SntOnOffSwitch";
import { hasPermission } from "@/hooks/usePermissions";
import { hasFeature } from "@/hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import type { LoginInfo } from "@/types/LoginInfo";
import type { SearchHistoryItem } from "@/containers/AdminLayout/SearchPanel/types";

const PAGE_SIZE = 50;
const PAGE_JUMP = 10;
// Reveal the next page of pill rows once the list is scrolled this close to its end.
const PILL_LOAD_MORE_THRESHOLD_PX = 100;

// Below this viewport width the pill drops its "Partner ▸" breadcrumb prefix and
// shows just the org (the full chain stays in the popover). This is the pill's
// own threshold: the header's right-hand side no longer has a matching one, it
// measures what fits (see Header.tsx) — and a narrower pill is simply more room
// for it to work with.
const HEADER_COMPACT_BELOW = 1500;

// --- Shared styled components ---

interface StyleCustomInfoBtnProps {
  $hoverColor?: string;
}

const StyleCustomInfoBtn = styled.div<StyleCustomInfoBtnProps>`
  button:hover {
    background-color: ${(props) => props.$hoverColor || ""};
  }
`;

// --- Pill variant styled components ---

const PillChevron = styled.svg<{ $open: boolean }>`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  transition: transform 0.15s ease;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0)")};
`;

// z-index has to clear the parent menu's stacking context. The system menu
// that hosts this org selector renders above Bootstrap's 1050 modal range,
// so 1060 isn't enough — the popover would render on top visually (because
// the portal puts it later in the DOM) but hit-testing would route taps to
// the menu items underneath, focusing them and scrolling the menu.
const PillPanel = styled.div`
  position: fixed;
  z-index: 100000;
  width: 480px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 120px);
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
`;

const PillBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: transparent;
`;

const PillSearchRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e4e7ec;
  padding-right: 8px;
  gap: 6px;
`;

const PillSearch = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: none;
  font-size: 16px;
  outline: none;
  background: transparent;
`;

const PillList = styled.div`
  max-height: min(480px, calc(100vh - 220px));
  overflow-y: auto;
`;

const PillItem = styled.div<{
  $selected: boolean;
  $highlighted?: boolean;
  $locked?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  /* A locked org is not a normal destination — muting the row keeps it visually
     subordinate to the active orgs it sits between in the alphabetical list. */
  opacity: ${(props) => (props.$locked ? 0.72 : 1)};
  background: ${(props) =>
    props.$highlighted
      ? props.$selected
        ? "#dbe4ff"
        : "#dde4ec"
      : props.$selected
        ? "#eff4ff"
        : "transparent"};

  &:hover {
    background: ${(props) => (props.$selected ? "#dbe4ff" : "#f9fafb")};
  }
`;

const PillItemSub = styled.div`
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PillSectionLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.5;
  letter-spacing: 0.03em;
`;

const PillRecentSection = styled.div`
  background: #f0f4f8;
`;

const PillDivider = styled.div`
  border-top: 1px solid #e4e7ec;
  margin: 4px 0;
`;

// --- Shared helpers ---

interface OrganizationOption {
  label: string;
  value: number;
  type: string;
  commercialPartner?: boolean;
  partnerName?: string;
  partnerVariant?: PartnerVariant;
  partnerType?: string;
  orgCount?: number;
  webDomain?: string;
  /**
   * STIC-15975: the org is LOCKED (deactivated). Only ever true for a SYSTEM
   * login, which is the only one that may browse locked orgs.
   */
  locked?: boolean;
}

// Match an org/partner row against the search text by NAME or numeric ORG ID.
// STIC-15665: the legacy react-select selector matched both (its default filter
// stringifies `${label} ${value}`), so typing an org id found the org. The
// redesigned selector's hand-rolled filter only checked the label — this
// restores id search.
const matchesOrgSearch = (item: OrganizationOption, searchText: string) => {
  const query = searchText.trim().toLowerCase();
  if (!query) return true;
  return (
    item.label.toLowerCase().includes(query) ||
    String(item.value).includes(query)
  );
};

interface OrganizationBasicInfo {
  id: number;
  name: string;
  partnerName?: string;
  organisationType: string;
  commercialPartner?: boolean;
  partnerVariant?: PartnerVariant;
  partnerType?: string;
  webDomain?: string;
  status?: "ACTIVE" | "LOCKED" | "DELETED";
}

interface Section {
  id: string;
  title: string;
  isShow: boolean;
  icon: React.ReactNode;
}

interface OrgSelectorChangeData {
  value: number;
  /**
   * Which menu the selection should land in. The org id alone can't say: picking
   * the System org from the "System" tab means "show me System's own artifacts",
   * while every other selection means "browse this org's data" — two different
   * menus for what would otherwise look like the same change.
   *
   * Omitted for plain dropdown picks, which are always cross-level.
   */
  scope?: "own" | "cross";
}

// Channel partners show their SF partner type (VAR / MSP / INTEGRATOR / …);
// multi-org partners show a "Multi-org" badge. The old single-letter
// 'C' / 'M' badge was opaque to most colleagues.
// Only shown on partner rows: on an org row the variant belongs to the org's
// partner, not the org, and repeating it in the "Partner: x" sub-line read as a
// property of the org itself.
const PartnerTypeBadge = ({ option }: { option: OrganizationOption }) => {
  let variantLabel: string | null = null;
  if (option.partnerVariant === "CHANNEL_PARTNER") {
    variantLabel = option.partnerType ?? null;
  } else if (option.partnerVariant) {
    // STIC-16004: direct multi-org, indirect multi-org or the Sensolus dummy partner.
    variantLabel = partnerVariantShortLabel(option.partnerVariant, translate(LanguageKey.multi_org_key));
  }

  if (!variantLabel) return null;

  // Channel partners blue; multi-org stays grey.
  if (option.partnerVariant === "CHANNEL_PARTNER") {
    return <PrimaryTag tagName={variantLabel} size="sm" title={variantLabel} />;
  }
  return <GreyTag tagName={variantLabel} size="sm" title={variantLabel} />;
};

// STIC-15975: marks a LOCKED (deactivated) org, both on its dropdown row and on
// the trigger pill once selected — so it stays obvious throughout the
// investigation that this org is not a live one.
const LockedTag = () => (
  <DangerTag
    tagName={translate(LanguageKey.locked_key)}
    size="sm"
    title={translate(LanguageKey.organisation_locked_browsing_key)}
  />
);

// STIC-11419: the partner's stock organisation, holding its unsold devices.
const StockTag = () => <GreyTag tagName={translate(LanguageKey.stock_key)} size="sm" />;

// Compact "P" badge used by the "prefix-icon" variant — overlaid on the
// top-left corner of a partner row's avatar. Only rendered on partners; child
// orgs get no decorator (a badge on every row is visual noise).
// The defaults are the dropdown-row geometry; the trigger pill passes the
// smaller "tight" geometry below because it only has 4px of slack around its
// avatar and an overhanging badge would poke through the button border.
const PartnerDecorator = ({
  size = 16,
  top = -4,
  left = -4,
  ring = 2,
}: {
  size?: number;
  top?: number;
  left?: number;
  ring?: number;
}) => (
  <span
    style={{
      position: "absolute",
      top,
      left,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      borderRadius: Math.round(size / 4),
      background: "#0071a1",
      color: "#ffffff",
      fontSize: Math.round(size * 0.625),
      fontWeight: 700,
      lineHeight: 1,
      boxShadow: `0 0 0 ${ring}px #ffffff`,
      pointerEvents: "none",
    }}
    title="Partner"
    aria-label="Partner"
  >
    P
  </span>
);

// Avatar + partner decorator, sharing the position-relative wrapper. Used by
// both the dropdown row and the trigger pill so partner rows read the same in
// every surface. A stock organisation shows the stock icon instead of its
// initials (STIC-11419). `tight` shrinks the badge and keeps it clear of the container
// edges, for containers with little slack (the 36px-high trigger pill).
const OrgIconWithPartnerBadge = ({
  option,
  size,
  showPartnerBadge,
  tight = false,
}: {
  option: OrganizationOption;
  size: number;
  showPartnerBadge: boolean;
  tight?: boolean;
}) => (
  <span
    style={{
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 0,
    }}
  >
    {option.type === OrganizationType.STOCK ? (
      <span
        style={{
          width: size,
          height: size,
          // STIC-11419: stock reads as its own category, so the tile is tinted
          // grey instead of the white used for a normal organization's avatar.
          background: "var(--sensolus-Sensolus_BG_LightGrey)",
          border: "1px solid var(--sensolus-Sensolus_Grey_Lighter)",
          borderRadius: 6,
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--sensolus-Sensolus_Black)",
        }}
      >
        <SntStockOrgIcon size={Math.round(size / 2)} />
      </span>
    ) : (
      <SntOrgBrandIcon domain={option.webDomain} name={option.label} size={size} />
    )}
    {showPartnerBadge &&
      (tight ? (
        // The pill has only 3px of slack above the avatar, so the badge
        // overhangs sideways (plenty of button padding there) instead of
        // upwards, where it would cut through the button's top border.
        <PartnerDecorator size={14} top={-1.5} left={-4} ring={1.5} />
      ) : (
        <PartnerDecorator />
      ))}
  </span>
);

// --- Component ---

interface SntOrgMenuSelectorContainerProps {
  value: number | null;
  onChange?: (data: OrgSelectorChangeData) => void;
  disabled?: boolean;
  variant?: "classic" | "pill";
  hideImpersonate?: boolean;
  /**
   * Keep the pill variant on a single line, truncating the org name when it
   * runs out of room. For the header, whose row is 56px tall: a wrap there put
   * the pill under the tabs and left the whole header overflowing its own box.
   * The mobile sidebar renders the same control in a ~250px column and does
   * want the wrap, so this is opt-in.
   */
  singleLine?: boolean;
}

const SntOrgMenuSelectorContainer = ({
  value,
  onChange,
  disabled = false,
  variant = "classic",
  hideImpersonate = false,
  singleLine = false,
}: SntOrgMenuSelectorContainerProps) => {
  const loginInfo = useSelector(userSelector) as LoginInfo;
  const { onImpersonateUser, IMPERSONATE_TYPE } = useUser();
  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({});
  const { mergeDataWithValueDefault } = useCustomBranding();
  const { width: windowWidth } = useWindowSize();
  const compact = windowWidth < HEADER_COMPACT_BELOW;

  const customStyle = useMemo(() => {
    let custumInfo = {};
    if (loginInfo && loginInfo.custumInfo) {
      custumInfo = loginInfo.custumInfo;
    }
    return mergeDataWithValueDefault(custumInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  // Org|Partner|All selector (see .snt-org-selector rules in sidebar.scss): every org
  // gets transparent inactive tabs + an active tab filled with the selected-menu-item
  // color (white on the default sidebar, the partner's color when branded). Only a
  // custom-branded org sets an inline text color (its section-font color) and gets the
  // .snt-org-selector-branded text override on the active/hover fill; a non-branded org
  // leaves the text as the greylight navy ($sensolus-primary #212851, i.e. dark blue).
  const hasCustomBranding = !!loginInfo?.custumInfo?.adminSideBarColor;

  const [sections, setSections] = useState<Section[]>([]);
  const [mode, setMode] = useState<string>();

  // Classic variant state
  const [allItems, setAllItems] = useState<OrganizationOption[]>([]);
  const [visibleItems, setVisibleItems] = useState<OrganizationOption[]>([]);
  const searchTextRef = useRef("");

  const [orgList, setOrgList] = useState<OrganizationOption[]>([]);
  const [partnerList, setPartnerList] = useState<OrganizationOption[]>([]);
  const [systemOrg, setSystemOrg] = useState<OrganizationOption | null>(null);
  const orgSelectedRef = useRef<number | undefined>(undefined);
  const partnerSelectedRef = useRef<number | undefined>(undefined);

  // The organisation whose "own level" this login owns — Sensolus for SYSTEM
  // logins, the partner's own org for PARTNER logins. It backs the first pill
  // tab ("System" / "Partner"), which is how you reach the own-level menu.
  // A partner's own org is a PARTNER type, so it also sits in partnerList.
  const isPartnerLogin = loginInfo.organisation?.organisationType === "PARTNER";
  const isSystemUser = loginInfo.organisation?.organisationType === "SYSTEM";
  const ownOrg = useMemo(() => {
    if (!isPartnerLogin) return systemOrg;
    return partnerList.find((p) => p.value === loginInfo.organisation?.id) ?? null;
  }, [isPartnerLogin, systemOrg, partnerList, loginInfo.organisation?.id]);
  const ownTabLabel = isPartnerLogin
    ? translate(LanguageKey.part_key)
    : translate(LanguageKey.system_key);

  /**
   * The partners this login may actually select — none, for a partner login.
   *
   * A partner uses the selector for one thing: picking one of its own child
   * organisations. Other partners are not its business, and its *own* org is
   * reached through the first tab rather than the list, so offering partner rows
   * at all is noise (STIC-15615).
   *
   * `partnerList` itself is deliberately left whole: `ownOrg` above resolves the
   * own tab out of it, so emptying it would break the tab this trims in favour
   * of.
   */
  const selectablePartners = useMemo(
    () => (isPartnerLogin ? [] : partnerList),
    [isPartnerLogin, partnerList]
  );

  const [isLoadingData, setLoadingData] = useState(true);
  const [isFirstLoaded, setFirstLoaded] = useState(true);

  // Pill variant state
  const lastNonSystemValueRef = useRef<number>(0);
  const autoOpenPillRef = useRef(false);
  const [pillOpen, setPillOpen] = useState(false);
  const [pillPos, setPillPos] = useState({ top: 0, left: 0 });
  const pillTriggerRef = useRef<HTMLButtonElement>(null);
  const pillPopoverRef = useRef<HTMLDivElement>(null);
  const pillSearchRef = useRef<HTMLInputElement>(null);
  const [pillSearch, setPillSearch] = useState("");
  const [pillPartnersOnly, setPillPartnersOnly] = useState(false);
  // STIC-15975: opt-in, never sticky — browsing a locked org is a deliberate
  // one-off, so every fresh open of the popover starts from the normal list.
  const [pillShowLocked, setPillShowLocked] = useState(false);
  const [pillHighlightIdx, setPillHighlightIdx] = useState(-1);
  const [pillVisibleCount, setPillVisibleCount] = useState(PAGE_SIZE);
  const pillListRef = useRef<HTMLDivElement>(null);
  const [recentEntries, setRecentEntries] = useState<Array<{ id: number; type: "PARTNER" | "ORG" }>>([]);

  // --- Shared impersonate handler ---

  const canImpersonate =
    mode !== "ALL" &&
    (hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY) ||
      hasPermission(UserPermission.ORG_SUPPORT_LOGIN_ADMIN)) &&
    hasFeature(FeatureKey.impersonate);

  const impersonateTarget = useMemo(() => {
    if (!value) return null;
    return [...orgList, ...partnerList].find((o) => o.value === value) || null;
  }, [value, orgList, partnerList]);

  const handleImpersonate = useCallback(() => {
    if (value === null) return;
    let impersonateMode = IMPERSONATE_TYPE.VIEWER;
    if (!hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY)) {
      impersonateMode = IMPERSONATE_TYPE.ADMIN;
    }
    onImpersonateUser({
      id: value,
      type: impersonateMode,
      onBadRequest: () => {
        openErrorDialog(translate(LanguageKey.impersonation_insufficient_rights_key));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const renderImpersonateButton = () => {
    if (!canImpersonate) return null;
    const isTargetSystem = impersonateTarget?.type === "SYSTEM";
    const title = isTargetSystem
      ? translate(LanguageKey.support_login_on_system_account_is_not_permitted_key)
      : translate(LanguageKey.support_login_key);
    const icon = <SntSupportUserIcon color="rgb(255, 168, 88)" style={{ opacity: isTargetSystem ? 0.4 : 1 }} />;
    const handleClick = isTargetSystem ? () => {} : handleImpersonate;

    if (variant === "pill") {
      return (
        <HeaderIconButton title={title} onClick={handleClick}>
          {icon}
        </HeaderIconButton>
      );
    }

    return (
      <Button
        variant="sensolus-greylight"
        disabled={isTargetSystem}
        className="ms-1"
        style={{
          color: customStyle.adminSideBarSectionFontColor,
          borderColor: customStyle.adminSideBarSectionFontColor,
        }}
        title={isTargetSystem ? title : undefined}
        onClick={handleClick}
      >
        {icon}
      </Button>
    );
  };

  // Closing on outside-click is handled by <PillBackdrop> inside the portal —
  // no document-level listener (those race with focus on mobile and tend to
  // close the popover the instant the user taps the search input).

  // Fetch recently viewed orgs/partners when pill opens
  useEffect(() => {
    if (variant !== "pill" || !pillOpen) return;
    globalSearchClient
      .getSearchHistory()
      .then(({ data }: { data: SearchHistoryItem[] }) => {
        // Preserve backend order (lastSeen DESC) so the merged recent list blends
        // partners and orgs by timestamp instead of grouping partners first.
        const entries: Array<{ id: number; type: "PARTNER" | "ORG" }> = [];
        for (const item of data) {
          const cat = item.searchIndex.category;
          const id = Number(item.searchIndex.entityId);
          if (cat === "Organization") {
            // Orgs may also appear as partners in the pill
            entries.push({ id, type: selectablePartners.some((p) => p.value === id) ? "PARTNER" : "ORG" });
          } else if (cat === "Partner") {
            entries.push({ id, type: "PARTNER" });
          }
        }
        // Drop recents this login can no longer pick — a partner's history still
        // holds partner entries from before the list was trimmed.
        setRecentEntries(
          isPartnerLogin ? entries.filter((e) => e.type !== "PARTNER") : entries
        );
      })
      .catch(() => {
        // silently ignore - recents are non-critical
      });
  }, [variant, pillOpen, selectablePartners, isPartnerLogin]);

  // After the SYSTEM→Organizations toggle re-mounts the pill, open the popover
  // anchored to its now-visible trigger. Has to wait for the trigger ref to
  // populate — toggling state alone doesn't give us a layout pass.
  useEffect(() => {
    if (variant !== "pill" || !autoOpenPillRef.current || mode === "OWN" || !mode) return;
    if (!pillTriggerRef.current) return;
    autoOpenPillRef.current = false;
    const rect = pillTriggerRef.current.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - 488);
    setPillPos({ top: rect.bottom + 4, left: Math.max(8, left) });
    setPillSearch("");
    setPillPartnersOnly(false);
    setPillShowLocked(false);
    setPillOpen(true);
  }, [variant, mode]);

  // Focus the search input when the popover opens. Critical because the
  // popover is portaled to document.body — without explicit focus, Tab from
  // the trigger walks through every focusable element on the page before
  // reaching the popover (which lives at the end of the DOM).
  useEffect(() => {
    if (!pillOpen) return;
    const id = setTimeout(() => pillSearchRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [pillOpen]);

  // Unified list: partners + orgs merged alphabetically so a single search box
  // matches either. The PARTNER/ORG tab split is gone — type is conveyed by the
  // row's badge (partner) or partner-context sub-text (org).
  // STIC-11419: a partner's stock organisation is pinned right under "All"
  // instead of sitting in the alphabetical list.
  const pinnedStockItem = useMemo(
    () => (isPartnerLogin ? orgList.find((org) => org.type === OrganizationType.STOCK) ?? null : null),
    [isPartnerLogin, orgList]
  );

  const pillCombinedItems = useMemo(() => {
    if (variant !== "pill") return [];
    return [...selectablePartners, ...orgList.filter((org) => org !== pinnedStockItem)].sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }, [variant, selectablePartners, orgList, pinnedStockItem]);

  // Every row matching the current search and switches. Only the first
  // pillVisibleCount of them are rendered; scrolling (or arrowing) towards the
  // end reveals the next page, so orgs further down the alphabet stay reachable.
  const pillMatchingItems = useMemo(() => {
    if (variant !== "pill") return [];
    // When "Partners only" is on, drop non-partner rows.
    let base = pillPartnersOnly
      ? pillCombinedItems.filter((item) => item.type === "PARTNER")
      : pillCombinedItems;
    // STIC-15975: locked orgs are held back until the "Locked" switch is on. The
    // currently selected one is always kept, so turning the switch back off
    // can't make the org you are browsing vanish from its own list.
    if (!pillShowLocked) {
      base = base.filter((item) => !item.locked || item.value === value);
    }
    if (!pillSearch) return base;
    return base.filter((item) => matchesOrgSearch(item, pillSearch));
  }, [variant, pillCombinedItems, pillSearch, pillPartnersOnly, pillShowLocked, value]);

  const pillFilteredItems = useMemo(
    () => pillMatchingItems.slice(0, pillVisibleCount),
    [pillMatchingItems, pillVisibleCount]
  );

  const pillHasMore = pillFilteredItems.length < pillMatchingItems.length;

  const loadMorePillItems = useCallback(() => {
    if (pillHasMore) setPillVisibleCount((prev) => prev + PAGE_SIZE);
  }, [pillHasMore]);

  const handlePillListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < PILL_LOAD_MORE_THRESHOLD_PX) {
      loadMorePillItems();
    }
  };

  const pillRecentItems = useMemo(() => {
    if (variant !== "pill" || pillSearch) return [];
    if (recentEntries.length === 0) return [];
    const seen = new Set<number>();
    const items: OrganizationOption[] = [];
    for (const { id } of recentEntries) {
      if (seen.has(id)) continue;
      seen.add(id);
      if (id === value) continue; // skip the currently selected scope — already shown in the pill
      const found = pillCombinedItems.find((o) => o.value === id);
      if (!found) continue;
      if (pillPartnersOnly && found.type !== "PARTNER") continue;
      // A recently-viewed locked org stays hidden until the switch is on, so the
      // switch is the single control over whether locked orgs are on screen.
      if (found.locked && !pillShowLocked) continue;
      items.push(found);
    }
    return items.slice(0, 5);
  }, [variant, pillSearch, recentEntries, pillCombinedItems, value, pillPartnersOnly, pillShowLocked]);

  // Pseudo "All" entry pinned at the top of the unified list — selecting it
  // sets value=0 ("across all organizations"). Globe icon signals this is a
  // special scope, not a real org row.
  const allPseudoItem = useMemo<OrganizationOption>(
    () => ({
      label: translate(LanguageKey.all_organizations_key),
      value: 0,
      type: "ALL",
    }),
    []
  );

  const showAllPseudoItem = useMemo(() => {
    if (pillPartnersOnly) return false;
    if (!pillSearch) return true;
    return allPseudoItem.label.toLowerCase().includes(pillSearch.toLowerCase());
  }, [pillSearch, allPseudoItem, pillPartnersOnly]);

  const showPinnedStock = useMemo(() => {
    if (!pinnedStockItem || pillPartnersOnly) return false;
    return matchesOrgSearch(pinnedStockItem, pillSearch);
  }, [pinnedStockItem, pillPartnersOnly, pillSearch]);

  // Combined list of all navigable items for keyboard navigation
  const pillAllNavigableItems = useMemo(() => {
    const items: OrganizationOption[] = [];
    if (showAllPseudoItem) items.push(allPseudoItem);
    if (showPinnedStock && pinnedStockItem) items.push(pinnedStockItem);
    items.push(...pillRecentItems, ...pillFilteredItems);
    return items;
  }, [showAllPseudoItem, allPseudoItem, showPinnedStock, pinnedStockItem, pillRecentItems, pillFilteredItems]);

  // Reset highlight and paging when the popover opens or the search text or
  // either filter changes
  useEffect(() => {
    setPillHighlightIdx(-1);
    setPillVisibleCount(PAGE_SIZE);
  }, [pillOpen, pillSearch, pillPartnersOnly, pillShowLocked]);

  // Keyboard navigation reaching the last rendered rows reveals the next page,
  // the same way scrolling does
  useEffect(() => {
    if (pillHighlightIdx >= pillAllNavigableItems.length - PAGE_JUMP) {
      loadMorePillItems();
    }
  }, [pillHighlightIdx, pillAllNavigableItems.length, loadMorePillItems]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (pillHighlightIdx < 0 || !pillListRef.current) return;
    const items = pillListRef.current.querySelectorAll("[data-pill-item]");
    items[pillHighlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [pillHighlightIdx]);

  const handlePillSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const itemCount = pillAllNavigableItems.length;
    if (itemCount === 0) return;

    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      setPillHighlightIdx((prev) => (prev < itemCount - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setPillHighlightIdx((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "PageDown") {
      e.preventDefault();
      setPillHighlightIdx((prev) => Math.min(itemCount - 1, Math.max(prev, 0) + PAGE_JUMP));
    } else if (e.key === "PageUp") {
      e.preventDefault();
      setPillHighlightIdx((prev) => Math.max(0, Math.max(prev, 0) - PAGE_JUMP));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (pillHighlightIdx >= 0 && pillHighlightIdx < itemCount) {
        handlePillItemClick(pillAllNavigableItems[pillHighlightIdx]);
      } else if (itemCount === 1) {
        handlePillItemClick(pillAllNavigableItems[0]);
      }
    } else if (e.key === "Escape") {
      setPillOpen(false);
    }
  };

  // --- Sync mode from value ---

  useEffect(() => {
    if (isLoadingData) return;
    if (!value) {
      setMode("ALL");
      lastNonSystemValueRef.current = 0;
    } else if (ownOrg && ownOrg.value === value) {
      setMode("OWN");
    } else {
      lastNonSystemValueRef.current = value;
      const find = orgList.find((data) => data.value === value);
      if (find) {
        orgSelectedRef.current = value;
        setMode("ORG");
        searchTextRef.current = "";
        // The classic variant offers no way to opt into locked orgs, so it lists
        // the active ones only — matching its behaviour before STIC-15975.
        setAllItems(orgList.filter((o) => !o.locked || o.value === value));
      } else {
        const partnerFind = partnerList.find((data) => data.value === value);
        if (partnerFind) {
          partnerSelectedRef.current = value;
          setMode("PARTNER");
          searchTextRef.current = "";
          setAllItems(partnerList.filter((p) => !p.locked || p.value === value));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isLoadingData]);

  // --- Load org data ---

  useEffect(() => {
    let existOrg: OrganizationOption | undefined = undefined;
    if (value) {
      existOrg = orgList.find((data) => data.value === value);
      if (!existOrg) {
        existOrg = partnerList.find((data) => data.value === value);
      }
      if (!existOrg && systemOrg?.value === value) {
        existOrg = systemOrg;
      }
      if (!existOrg) {
        setLoadingData(true);
        getOrgList();
      }
    } else if (isFirstLoaded) {
      getOrgList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getOrgList = () => {
    organizationClient
      // STIC-15975: locked orgs are fetched up front for SYSTEM (the backend
      // ignores the flag for anyone else) and then held back by the "Locked"
      // switch below. Fetching once keeps the switch instant and, more
      // importantly, lets a `?org=<locked id>` deep link resolve its name and
      // locked state even though the row was never listed.
      .getBasicInfo(isSystemUser)
      .then(({ data }: { data: OrganizationBasicInfo[] }) => {
        const listOrg: OrganizationOption[] = [];
        const listPartner: OrganizationOption[] = [];
        let sysOrg: OrganizationOption | null = null;
        data.forEach((item) => {
          const option: OrganizationOption = {
            label: item.name,
            value: item.id,
            type: item.organisationType,
            commercialPartner: item.commercialPartner,
            partnerName: item.partnerName,
            partnerVariant: item.partnerVariant,
            partnerType: item.partnerType,
            webDomain: item.webDomain,
            locked: item.status === "LOCKED",
          };
          if (option.type === "SYSTEM") {
            sysOrg = option;
          } else if (option.type === "PARTNER") {
            option.label = item.partnerName || item.name;
            listPartner.push(option);
          } else {
            listOrg.push(option);
          }
        });

        // Locked orgs are excluded from a partner's "n organizations" sub-line:
        // the count reads as "how big is this partner", which locked (i.e.
        // deactivated) orgs no longer contribute to.
        const orgCountByPartner: Record<string, number> = {};
        listOrg.forEach((org) => {
          if (org.partnerName && !org.locked && org.type !== OrganizationType.STOCK) {
            orgCountByPartner[org.partnerName] = (orgCountByPartner[org.partnerName] || 0) + 1;
          }
        });
        listPartner.forEach((partner) => {
          partner.orgCount = orgCountByPartner[partner.label] || 0;
        });

        setOrgList(listOrg);
        setPartnerList(listPartner);
        setSystemOrg(sysOrg);
        // The classic variant has no "Locked" switch, so its tabs count only the
        // orgs it can actually offer.
        const activeOrgCount = listOrg.filter((o) => !o.locked).length;
        const activePartnerCount = listPartner.filter((p) => !p.locked).length;
        const sectionItems: Section[] = [
          { id: "ORG", title: translate(LanguageKey.org_key), isShow: activeOrgCount !== 0, icon: <SntOrgIcon /> },
          {
            id: "PARTNER",
            title: translate(LanguageKey.part_key),
            // A partner login has no partners to browse — only its child orgs.
            isShow:
              activePartnerCount !== 0 &&
              loginInfo.organisation?.organisationType !== "PARTNER",
            icon: <SntPartnersIcon />,
          },
          { id: "ALL", title: translate(LanguageKey.all_key), isShow: true, icon: <SntGlobeIcon /> },
          { id: "SYSTEM", title: translate(LanguageKey.system_key), isShow: sysOrg !== null, icon: <SntSystemDevIcon /> },
        ];
        if (loginInfo.organisation?.organisationType === "SYSTEM") {
          sectionItems.reverse();
        }
        setSections(sectionItems);
        setLoadingData(false);
        setFirstLoaded(false);
      })
      .catch(() => {
        setLoadingData(false);
        setFirstLoaded(false);
      });
  };

  // --- Classic variant helpers ---

  const getFilteredItems = useCallback(
    (sourceItems: OrganizationOption[], searchText: string) => {
      if (!searchText) return sourceItems;
      return sourceItems.filter((item) => matchesOrgSearch(item, searchText));
    },
    []
  );

  useEffect(() => {
    const filtered = getFilteredItems(allItems, searchTextRef.current);
    setVisibleItems(filtered.slice(0, PAGE_SIZE));
  }, [allItems, getFilteredItems]);

  const onMenuScrollToBottom = useCallback(() => {
    const filtered = getFilteredItems(allItems, searchTextRef.current);
    if (visibleItems.length < filtered.length) {
      setVisibleItems(filtered.slice(0, visibleItems.length + PAGE_SIZE));
    }
  }, [allItems, visibleItems.length, getFilteredItems]);

  const onInputChange = useCallback(
    (text: string) => {
      searchTextRef.current = text;
      const filtered = getFilteredItems(allItems, text);
      setVisibleItems(filtered.slice(0, PAGE_SIZE));
    },
    [allItems, getFilteredItems]
  );

  const selectChanged = (item: SntSelectOption<number> | null) => {
    if (!item) return;
    onChange?.({ value: item.value });
  };

  // --- Pill variant helpers ---

  const pillLabel = useMemo(() => {
    if (mode === "ALL") return translate(LanguageKey.all_key);
    if (mode === "OWN") return ownTabLabel;
    if (mode === "PARTNER") {
      const found = partnerList.find((d) => d.value === value);
      return found?.label || translate(LanguageKey.all_key);
    }
    const found = orgList.find((d) => d.value === value);
    return found?.label || translate(LanguageKey.all_key);
  }, [mode, value, partnerList, orgList, ownTabLabel]);

  // A partner login only sees its own organisations, so its own name would repeat on each
  const pillPartnerContext = useMemo(() => {
    if (mode !== "ORG" || isPartnerLogin) return null;
    const found = orgList.find((d) => d.value === value);
    if (!found?.partnerName) return null;
    return found;
  }, [mode, value, orgList, isPartnerLogin]);

  // Bordered white box wrapping a glyph — same look as the "All organizations"
  // / "System" dropdown rows. Keeps the pill trigger visually consistent with
  // the row that selected it.
  const PillIconBox = ({
    children,
    background = "var(--sensolus-Sensolus_White)",
    borderColor = "var(--sensolus-Sensolus_Grey_Light)",
  }: {
    children: React.ReactNode;
    background?: string;
    borderColor?: string;
  }) => (
    <span
      style={{
        width: 28,
        height: 28,
        background,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--sensolus-Sensolus_Black)",
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );

  const pillIcon = useMemo(() => {
    if (mode === "ALL")
      return (
        <PillIconBox
          background="var(--sensolus-Sensolus_Green_Lightest)"
          borderColor="var(--sensolus-Sensolus_Green_Light)"
        >
          <SntGlobeIcon />
        </PillIconBox>
      );
    if (mode === "OWN")
      return isPartnerLogin ? (
        <PillIconBox>
          <SntPartnersIcon />
        </PillIconBox>
      ) : (
        <SntSystemDevIcon />
      );
    // For PARTNER and ORG modes the brand-icon box is rendered inside
    // renderPillLabel via OrgIconWithPartnerBadge — matching how dropdown rows
    // look.
    return null;
  }, [mode, isPartnerLogin]);

  const handlePillTriggerClick = () => {
    if (disabled) return;
    if (!pillOpen && pillTriggerRef.current) {
      const rect = pillTriggerRef.current.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 488);
      setPillPos({ top: rect.bottom + 4, left: Math.max(8, left) });
      setPillSearch("");
      setPillPartnersOnly(false);
      setPillShowLocked(false);
    }
    setPillOpen((prev) => !prev);
  };

  const handlePillItemClick = (item: OrganizationOption) => {
    setPillOpen(false);
    setPillSearch("");
    onChange?.({ value: item.value });
  };

  // --- Pill trigger label ---

  const selectedOption = useMemo(() => {
    if (value === null || value === 0) return null;
    return (
      orgList.find((d) => d.value === value) ||
      partnerList.find((d) => d.value === value) ||
      (ownOrg && ownOrg.value === value ? ownOrg : null)
    );
  }, [value, orgList, partnerList, ownOrg]);

  // Read off the selected org itself rather than off the list state, so a
  // `?org=<locked id>` deep link is flagged too — that org was never listed.
  const selectedIsLocked = !!selectedOption?.locked;

  const renderPillLabel = () => {
    // The org name is the one part of the pill that may be arbitrarily long, so
    // it is what gives when the header runs short of room: an ellipsis here
    // keeps the pill on the tabs' line instead of wrapping under them and
    // pushing the whole header out of its 56px row. The full name stays in the
    // popover (and in the button's title).
    const label = singleLine ? (
      <span className="text-truncate" style={{ minWidth: 0 }}>
        {pillLabel}
      </span>
    ) : (
      pillLabel
    );
    // System has its own <> pillIcon — the brand-icon box would just add
    // meaningless "SA" initials next to it.
    // A selected partner keeps the same blue "P" decorator its dropdown row
    // had: without it the badge vanished on selection and the trigger no longer
    // said whether the current scope was a partner or one of its orgs.
    const orgIcon = selectedOption && mode !== "OWN" ? (
      <span
        className="me-1 d-inline-flex align-items-center"
        style={{ flexShrink: 0 }}
      >
        <OrgIconWithPartnerBadge
          option={selectedOption}
          size={28}
          showPartnerBadge={selectedOption.type === "PARTNER"}
          tight
        />
      </span>
    ) : null;
    if (pillPartnerContext) {
      // The partner-name prefix ("Partner ▸") is the first thing to drop when the
      // header runs out of room — when compact we show just the org (the full chain
      // is still available inside the popover). Otherwise the whole breadcrumb shows.
      return (
        <>
          {!compact && (
            <span className="d-inline-flex align-items-center gap-1">
              {pillPartnerContext.partnerName}{" "}
              <PartnerTypeBadge option={pillPartnerContext} />
              {" › "}
            </span>
          )}
          {orgIcon}
          {label}
        </>
      );
    }
    if (mode === "PARTNER") {
      const found = partnerList.find((d) => d.value === value);
      return (
        <span className="d-inline-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          {found && (
            <OrgIconWithPartnerBadge option={found} size={28} showPartnerBadge tight />
          )}
          {label}
          {found && <PartnerTypeBadge option={found} />}
        </span>
      );
    }
    return (
      <>
        {orgIcon}
        {label}
      </>
    );
  };

  // --- Pill popover content ---

  const renderPillItem = (item: OrganizationOption, navIndex: number, keyPrefix = "") => {
    const isAll = item.type === "ALL";
    const isPartner = item.type === "PARTNER";
    const isSelected = isAll
      ? value === 0 || value === null
      : item.value === value;
    const isHighlighted = navIndex === pillHighlightIdx;

    // A partner row is signalled by the blue "P" badge decorator on the avatar
    // (see OrgIconWithPartnerBadge below). The P already says "partner", so the
    // right-hand chip shows only the variant (Multi-org / VAR / ...).
    const showPartnerDecorator = isPartner;

    return (
      <PillItem
        key={`${keyPrefix}${item.value}`}
        data-pill-item
        $selected={isSelected}
        $highlighted={isHighlighted}
        $locked={item.locked}
        onClick={() => handlePillItemClick(item)}
        onMouseEnter={() => setPillHighlightIdx(navIndex)}
      >
        <div className="flex-shrink-0 me-2 d-flex align-items-center">
          {isAll ? (
            <span
              style={{
                width: 32,
                height: 32,
                // STIC-11419: a gentle green marks the cross-level scope apart
                // from the individual organizations listed below it.
                background: "var(--sensolus-Sensolus_Green_Lightest)",
                border: "1px solid var(--sensolus-Sensolus_Green_Light)",
                borderRadius: 6,
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--sensolus-Sensolus_Black)",
              }}
              aria-label="All organizations"
            >
              <SntGlobeIcon />
            </span>
          ) : (
            <OrgIconWithPartnerBadge
              option={item}
              size={32}
              showPartnerBadge={showPartnerDecorator}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2">
            <span className={`text-truncate ${isSelected ? "fw-bold" : ""}`}>
              {item.label}
            </span>
            {isPartner && (
              <div className="flex-shrink-0">
                <PartnerTypeBadge option={item} />
              </div>
            )}
            {item.type === OrganizationType.STOCK && (
              <div className="flex-shrink-0">
                <StockTag />
              </div>
            )}
            {item.locked && (
              <div className="flex-shrink-0">
                <LockedTag />
              </div>
            )}
          </div>
          {!isAll && isPartner && item.orgCount ? (
            <PillItemSub>
              {`${item.orgCount} ${item.orgCount === 1 ? translate(LanguageKey.organization_key).toLowerCase() : translate(LanguageKey.organizations_key).toLowerCase()}`}
            </PillItemSub>
          ) : !isAll && !isPartner && !isPartnerLogin ? (
            <PillItemSub className="text-truncate">
              {item.partnerName
                ? `${translate(LanguageKey.partner_key)}: ${item.partnerName}`
                : "Sensolus only"}
            </PillItemSub>
          ) : null}
        </div>
      </PillItem>
    );
  };

  const renderPillPopoverContent = () => {
    const allItemOffset = (showAllPseudoItem ? 1 : 0) + (showPinnedStock ? 1 : 0);
    return (
      <>
        <PillSearchRow>
          <PillSearch
            ref={pillSearchRef}
            placeholder={`${translate(LanguageKey.search_key)}...`}
            value={pillSearch}
            onChange={(e) => setPillSearch(e.target.value)}
            onKeyDown={handlePillSearchKeyDown}
          />
          {!isPartnerLogin && (
            <SntOnOffSwitch
              checked={pillPartnersOnly}
              onChange={setPillPartnersOnly}
              title={translate(LanguageKey.partners_only_key)}
            />
          )}
          {/* STIC-15975: SYSTEM-only — locked orgs are an investigation tool, and
              no other login type ever receives them from the backend. */}
          {isSystemUser && (
            <SntOnOffSwitch
              checked={pillShowLocked}
              onChange={setPillShowLocked}
              title={translate(LanguageKey.locked_key)}
            />
          )}
        </PillSearchRow>
        <PillList ref={pillListRef} onScroll={handlePillListScroll}>
          {showAllPseudoItem && renderPillItem(allPseudoItem, 0, "all-")}
          {showPinnedStock &&
            pinnedStockItem &&
            renderPillItem(pinnedStockItem, showAllPseudoItem ? 1 : 0, "stock-")}
          {(showAllPseudoItem || showPinnedStock) &&
            (pillRecentItems.length > 0 || pillFilteredItems.length > 0) && <PillDivider />}
          {pillRecentItems.length > 0 && (
            <PillRecentSection>
              <PillSectionLabel>{translate(LanguageKey.v41_recent_viewed_key)}</PillSectionLabel>
              {pillRecentItems.map((item, i) =>
                renderPillItem(item, allItemOffset + i, "recent-")
              )}
            </PillRecentSection>
          )}
          {pillRecentItems.length > 0 && pillFilteredItems.length > 0 && <PillDivider />}
          {pillFilteredItems.map((item, i) =>
            renderPillItem(item, allItemOffset + pillRecentItems.length + i)
          )}
        </PillList>
      </>
    );
  };

  const renderPillPopover = () => {
    if (!pillOpen) return null;
    return createPortal(
      <>
        <PillBackdrop
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setPillOpen(false);
          }}
        />
        <PillPanel
          ref={pillPopoverRef}
          data-pill-popover
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{ top: pillPos.top, left: pillPos.left }}
        >
          {renderPillPopoverContent()}
        </PillPanel>
      </>,
      document.body
    );
  };

  // --- Render mode switcher ---

  const renderModeSwitcher = () => {
    if (variant === "pill") {
      return (
        // singleLine (the header): flex-nowrap, because a wrap there dropped
        // the pill onto a second line under the tabs and left the whole header
        // two rows tall inside its one-row box, with the action icons stranded
        // in the middle. The pill truncates instead. Everywhere else — the
        // mobile sidebar's narrow column — wrapping is still the right answer.
        <div
          className={`d-flex align-items-center gap-2 ${
            singleLine ? "flex-nowrap" : "flex-wrap"
          }`}
          style={singleLine ? { minWidth: 0 } : undefined}
        >
          {ownOrg && (
            <SntButtonGroup<"ORG" | "OWN">
              style={singleLine ? { flexShrink: 0 } : undefined}
              value={mode === "OWN" ? "OWN" : "ORG"}
              onChange={(next) => {
                if (next === "OWN") {
                  setPillOpen(false);
                  onChange?.({ value: ownOrg.value, scope: "own" });
                } else {
                  autoOpenPillRef.current = true;
                  onChange?.({ value: lastNonSystemValueRef.current, scope: "cross" });
                }
              }}
              options={[
                // Own level carries the login's own glyph: the partners icon for a
                // partner, the <> glyph the sidebar uses for System. Not the Sensolus
                // cloud mark: the compact header already shows that one right next
                // to this control, and two of them side by side read as a repeat.
                {
                  value: "OWN",
                  label: ownTabLabel,
                  icon: isPartnerLogin ? <SntPartnersIcon /> : <SntSystemDevIcon />,
                },
                // Cross level is the "Organization explorer"; the tab shows the short
                // word next to the Organisations glyph, the full name is the tooltip.
                {
                  value: "ORG",
                  label: translate(LanguageKey.explorer_key),
                  title: translate(LanguageKey.organization_explorer_key),
                  icon: <SntOrgIcon />,
                },
              ]}
            />
          )}
          {mode !== "OWN" && (
            <Button
              ref={pillTriggerRef}
              variant="sensolus-greylight"
              onClick={handlePillTriggerClick}
              disabled={disabled}
              className="d-inline-flex align-items-center gap-2"
              // The name can be cut short by the ellipsis, so keep the whole
              // one reachable on hover.
              title={pillLabel}
              style={{
                height: 36,
                padding: "0 12px",
                fontSize: 14,
                whiteSpace: "nowrap",
                // Shrinkable, so the label's ellipsis can engage instead of the
                // row wrapping or the pill pushing the header wider.
                ...(singleLine && { minWidth: 0, overflow: "hidden" }),
                position: pillOpen ? "relative" : undefined,
                zIndex: pillOpen ? 1061 : undefined,
                // STIC-15975: a red rule down the leading edge, so the locked
                // state reads even at a glance where the badge is easy to skim
                // past. Border only — an inline box-shadow would beat
                // Bootstrap's :focus ring and swallow the focus indicator.
                ...(selectedIsLocked && {
                  borderColor: "var(--bs-danger)",
                  borderLeftWidth: 3,
                }),
              }}
            >
              {pillIcon}
              {renderPillLabel()}
              {selectedIsLocked && <LockedTag />}
              <PillChevron $open={pillOpen} viewBox="0 0 12 12">
                <path d="M2.5 4.5L6 8L9.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </PillChevron>
            </Button>
          )}
          {mode !== "OWN" && !hideImpersonate && renderImpersonateButton()}
          {renderPillPopover()}
        </div>
      );
    }

    // Classic ButtonGroup
    return (
      <ButtonGroup
        style={{ width: "100%" }}
        className={`snt-org-selector${hasCustomBranding ? " snt-org-selector-branded" : ""}`}
      >
        {sections
          .filter((item) => item.isShow)
          .map((item, itemIdx) => (
            <SntButton
              key={itemIdx}
              style={{
                color: hasCustomBranding ? customStyle.adminSideBarSectionFontColor : undefined,
                borderColor: hasCustomBranding ? customStyle.adminSideBarSectionFontColor : undefined,
                padding: "0.375rem",
              }}
              className={`${item.id === mode ? "active" : ""}`}
              onClick={() => {
                let val = 0;
                if (item.id === "ORG") {
                  val = orgSelectedRef.current || (orgList[0] ? orgList[0].value : 0);
                } else if (item.id === "PARTNER") {
                  val = partnerSelectedRef.current || (partnerList[0] ? partnerList[0].value : 0);
                } else if (item.id === "SYSTEM" && systemOrg) {
                  val = systemOrg.value;
                }
                onChange?.({ value: val });
              }}
              icon={item.icon}
              text={item.title}
            />
          ))}
      </ButtonGroup>
    );
  };

  // --- Classic variant: formatOptionLabel for SntSelect ---

  const formatOptionLabel = useCallback(
    (selectOption: SntSelectOption<number>, context: SntSelectLabelContext) => {
      const option =
        allItems.find((item) => item.value === selectOption.value) ||
        visibleItems.find((item) => item.value === selectOption.value);
      if (!option) return selectOption.label;
      if (context === "value") {
        if (mode === "ORG" && option.partnerName && isSystemUser) {
          return (
            <span className="d-inline-flex align-items-center gap-2">
              <span style={{ opacity: 0.6 }}>{option.partnerName}</span>
              <PartnerTypeBadge option={option} />
              <span style={{ opacity: 0.4 }}>›</span>
              <span>{option.label}</span>
            </span>
          );
        }
        if (mode === "PARTNER") {
          return (
            <span className="d-inline-flex align-items-center gap-2">
              <span>{option.label}</span>
              <PartnerTypeBadge option={option} />
            </span>
          );
        }
        return option.label;
      }
      if (!isSystemUser) return option.label;
      if (mode === "PARTNER") {
        return (
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{option.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }} className="text-truncate">
                {option.orgCount
                  ? `${option.orgCount} ${option.orgCount === 1 ? translate(LanguageKey.organization_key).toLowerCase() : translate(LanguageKey.organizations_key).toLowerCase()}`
                  : ""}
              </div>
            </div>
            <div className="flex-shrink-0 ms-2"><PartnerTypeBadge option={option} /></div>
          </div>
        );
      }
      return (
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-truncate">
            <div className="text-truncate">{option.label}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }} className="text-truncate">
              {option.partnerName
                ? `${translate(LanguageKey.partner_key)}: ${option.partnerName}`
                : "Sensolus only"}
            </div>
          </div>
        </div>
      );
    },
    [mode, isSystemUser, allItems, visibleItems]
  );

  if (variant === "pill") {
    return (
      <>
        {renderModeSwitcher()}
        <ErrorDialog />
      </>
    );
  }

  return (
    <StyleCustomInfoBtn $hoverColor={customStyle.adminSideBarSelectedColor}>
      <Row>
        <Col xs={12}>
          {renderModeSwitcher()}
        </Col>
        {(mode === "ORG" ||
            (mode === "PARTNER" && loginInfo.organisation?.organisationType !== "PARTNER")) && (
          <Col xs={12} className="mt-2">
            <div className="d-flex justify-content-between w-100">
              <div className="flex-grow-1 flex-shrink-1" style={{ minWidth: 0 }}>
                <SntSelectWidget
                  onChange={selectChanged}
                  options={visibleItems}
                  value={allItems.find((option) => option.value === value) ?? null}
                  disabled={disabled}
                  filterOption={() => true}
                  onInputChange={onInputChange}
                  onMenuScrollToBottom={onMenuScrollToBottom}
                  menuMinWidth={isSystemUser ? (mode === "PARTNER" ? 350 : 300) : undefined}
                  optionDensity={isSystemUser ? "compact" : "default"}
                  formatOptionLabel={formatOptionLabel}
                />
              </div>
              {renderImpersonateButton()}
            </div>
          </Col>
        )}
      </Row>
      <ErrorDialog />
    </StyleCustomInfoBtn>
  );
};

export default SntOrgMenuSelectorContainer;
